import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { CacheTags, PaginatedRequest, SomeRequest } from 'types';
import { WorkOrderSearchRecord } from './bindings/WorkOrderSearchRecord';
import { WorkOrderResponse } from './bindings/WorkOrderResponse';
import { FetchResponse } from './bindings/FetchResponse';
import { OdinBaseQuery } from './core';
import { PostWorkOrderRequest } from './bindings/PostWorkOrderRequest';
import { PutWorkOrderRequest } from './bindings/PutWorkOrderRequest';

export const workOrdersApi = createApi({
  reducerPath: 'workOrders',
  baseQuery: OdinBaseQuery,
  tagTypes: [CacheTags.workOrders, CacheTags.workOrderDetail],
  endpoints: (builder) => ({
    fetchSomeWorkOrders: builder.query<
      FetchResponse<WorkOrderSearchRecord>,
      SomeRequest
    >({
      query: (payload) => ({
        url: '/api/work-orders',
        method: 'GET',
        params: payload,
      }),
      providesTags: (payload) => {
        if (!payload?.items) return [];
        return payload.items.map(({ id }) => ({
          type: CacheTags.workOrders,
          id: id,
        }));
      },
    }),
    fetchWorkOrdersPage: builder.query<
      FetchResponse<WorkOrderSearchRecord>,
      PaginatedRequest
    >({
      query: (payload) => ({
        url: '/api/work-orders',
        method: 'GET',
        params: payload,
      }),
      providesTags: (payload) => {
        let returnValue = [
          { type: CacheTags.workOrders, id: CacheTags.partial },
        ];

        if (payload) {
          returnValue = [
            ...returnValue,
            ...payload.items.map(({ id }) => ({
              type: CacheTags.workOrders,
              id: id,
            })),
          ];
        }

        return returnValue;
      },
    }),
    fetchWorkOrderDetail: builder.query<WorkOrderResponse, string>({
      query: (payload) => ({
        url: `/api/work-orders/${payload}`,
        method: 'GET',
      }),
      providesTags: (payload) => {
        if (!payload) return [];
        return [{ type: CacheTags.workOrderDetail, id: payload.id }];
      },
    }),
    postWorkOrder: builder.mutation<WorkOrderResponse, PostWorkOrderRequest>({
      query: (payload) => ({
        url: '/api/work-orders',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result) => [
        { type: CacheTags.workOrders, id: CacheTags.partial },
        { type: CacheTags.workOrderDetail, id: result?.id },
      ],
    }),
    putWorkOrder: builder.mutation<WorkOrderResponse, PutWorkOrderRequest>({
      query: (payload) => ({
        url: `/api/work-orders/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        { type: CacheTags.workOrders, id: CacheTags.partial },
        { type: CacheTags.workOrders, id: payload.id },
        { type: CacheTags.workOrderDetail, id: payload.id },
      ],
    }),
  }),
});

export const {
  useFetchSomeWorkOrdersQuery,
  useFetchWorkOrdersPageQuery,
  useFetchWorkOrderDetailQuery,
  usePostWorkOrderMutation,
  usePutWorkOrderMutation,
} = workOrdersApi;
